import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { Band, Card } from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import KpiTile from '../../components/KpiTile';
import PageHeader from '../../components/PageHeader';
import Sparkline from '../../components/Sparkline';
import { formatCompactNumber, formatDuration, humanize } from '../../format';

/**
 * Growth › Overview — who is using MAHI, and how much.
 *
 * Aggregate by default, always: the product doc's Section 7 rule is that
 * drilling into one person is a deliberate, logged action rather than the
 * landing state, so a single user's data is not reachable from this page.
 * That lives one tab over, behind a search, gated to operator/support/owner.
 *
 * The session-length numbers are median and p90 rather than a mean. The old
 * page carried a caption saying "not a mean - see Section 5", which is a note
 * from one engineer to another; the useful version of that caveat is on the
 * tile, phrased for whoever is reading the number.
 */
function SourceBar({ source }) {
  const entries = Object.entries(source || {});
  if (entries.length === 0) {
    return <p className="text-sm text-ink-muted">No login data recorded yet.</p>;
  }
  const total = entries.reduce((sum, [, v]) => sum + v, 0) || 1;
  return (
    <div className="space-y-2.5">
      {entries.sort((a, b) => b[1] - a[1]).map(([key, count]) => (
        <div key={key}>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-ink-secondary">{humanize(key)}</span>
            <span className="tabular-nums text-ink-muted">
              {formatCompactNumber(count)} · {Math.round((100 * count) / total)}%
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-surface-page">
            <div
              className="h-1.5 rounded-full bg-raspberry-500"
              style={{ width: `${((100 * count) / total).toFixed(1)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GrowthOverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // This page shows no cohort table, so it asks for the smallest legal
        // slice of one rather than carrying the whole thing in the payload.
        const { data } = await adminApi.get('/users/aggregate', {
          params: { cohort_page_size: 1 },
        });
        setData(data);
      } catch (err) {
        toast.error(errMsg(err, 'Could not load growth data'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dau = data?.dau_session;
  const wauMau = data?.wau_mau;
  const trend = data?.signup_trend || [];
  const spark = trend.map((t) => t.signups);
  const stickiness = wauMau?.mau ? Math.round((100 * (dau?.dau || 0)) / wauMau.mau) : null;

  return (
    <div>
      <PageHeader
        title="Growth"
        hint="Everyone, in aggregate. Finding one person is a separate, logged action."
      />

      <Band title="Active users">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <KpiTile
            label="DAU"
            value={formatCompactNumber(dau?.dau)}
            loading={loading}
            caption={dau?.date ? `as of ${dau.date}` : 'Nightly rollup — yesterday is the latest complete day.'}
          />
          <KpiTile label="WAU" value={formatCompactNumber(wauMau?.wau)} loading={loading} caption="Trailing 7 days" />
          <KpiTile label="MAU" value={formatCompactNumber(wauMau?.mau)} loading={loading} caption="Trailing 30 days" />
          <KpiTile
            label="Median session"
            value={formatDuration(dau?.median_session_s)}
            loading={loading}
            caption="Half of sessions are shorter than this."
          />
          <KpiTile
            label="p90 session"
            value={formatDuration(dau?.p90_session_s)}
            loading={loading}
            caption="The engaged tail — one in ten sessions runs longer."
          />
        </div>
        {stickiness != null && (
          <p className="mt-2 text-xs text-ink-muted">
            DAU/MAU stickiness: <span className="font-medium text-ink-secondary">{stickiness}%</span> — the share
            of monthly users who came back on the latest day.
          </p>
        )}
      </Band>

      <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card
          title="Signups"
          hint="Daily, from the nightly rollup"
          className="lg:col-span-2"
        >
          {loading ? (
            <p className="text-sm text-ink-muted">Loading…</p>
          ) : trend.length === 0 ? (
            <EmptyState title="No signup history yet" detail="The nightly rollup has not written a signup count for any day in range." />
          ) : (
            <>
              <div className="flex h-24 items-end gap-0.5">
                {trend.map((t) => {
                  const max = Math.max(1, ...spark);
                  return (
                    <div
                      key={t.date}
                      className="group relative flex-1"
                      title={`${t.date}: ${t.signups} signup${t.signups === 1 ? '' : 's'}`}
                    >
                      <div
                        className="rounded-t bg-raspberry-400 transition-colors group-hover:bg-raspberry-600"
                        style={{ height: `${Math.max(2, (t.signups / max) * 88)}px` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex justify-between text-[11px] text-ink-muted">
                <span>{trend[0]?.date}</span>
                <span>{trend[trend.length - 1]?.date}</span>
              </div>
            </>
          )}
        </Card>

        <Card title="Where they sign in from" hint="Trailing 7 days">
          {loading ? <p className="text-sm text-ink-muted">Loading…</p> : <SourceBar source={dau?.login_source} />}
        </Card>
      </div>

      <Band title="Recent signup shape">
        <Card>
          <div className="flex items-center gap-4">
            <div className="min-w-0 flex-1">
              <Sparkline values={spark.slice(-30)} height={40} color="#C42569" />
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-xs text-ink-muted">Last 30 days</div>
              <div className="text-lg font-semibold tabular-nums text-ink">
                {formatCompactNumber(spark.slice(-30).reduce((a, b) => a + b, 0))}
              </div>
            </div>
          </div>
        </Card>
      </Band>
    </div>
  );
}
